import { Brightness1Outlined, CloseOutlined } from "@mui/icons-material";
import {
  ListItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useState } from "react";
import HelpDialogLayout, {
  HelpDialogButton,
  HelpDialogLayoutProps,
  HelpDialogList,
} from "./HelpDialogLayout";

export interface SleepFactorJudgementCriteriaDialogProps
  extends Omit<HelpDialogLayoutProps, "children" | "title"> {}

export function SleepFactorJudgementCriteriaDialog(
  props: SleepFactorJudgementCriteriaDialogProps
) {
  return (
    <HelpDialogLayout title="睡眠指標の判定について" {...props}>
      <Stack gap={6}>
        <Stack gap={2}>
          <Typography variant="body2">
            睡眠を「長さ」、「質」、「リズム」の3つの指標に分解してそれぞれを評価しています。
          </Typography>
          <HelpDialogList>
            <ListItem>
              <Typography variant="body2">
                睡眠の長さ：睡眠時間が適切か
              </Typography>
            </ListItem>
            <ListItem>
              <Typography variant="body2">
                睡眠の質：寝付きや睡眠中の覚醒が適正か
              </Typography>
            </ListItem>
            <ListItem>
              <Typography variant="body2">
                睡眠のリズム：日々の起床時刻にバラつきがないか
              </Typography>
            </ListItem>
          </HelpDialogList>
          <Typography variant="body2">
            ※各指標の詳細な判定基準については、各睡眠指標の判定付近にある[詳細を見る]からご確認ください。
          </Typography>
          <Typography variant="body2">
            各指標の日別判定に基づき、測定期間を通しての判定結果を「睡眠指標の判定」として表示しています。
          </Typography>
        </Stack>
        <SleepFactorJudgementCriteriaTable />
        <Stack>
          <Typography variant="caption">
            ※測定日数が7日間の場合となります。測定日数が4～6日の場合は「レポートの読み方」をご確認ください。
          </Typography>
          <Typography variant="caption">
            ※測定日数が3日以下の場合は判定不能となります。
          </Typography>
        </Stack>
      </Stack>
    </HelpDialogLayout>
  );
}

export default function SleepFactorJudgementCriteriaDialogButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <HelpDialogButton onClick={() => setOpen(true)}>
        睡眠指標の判定基準
      </HelpDialogButton>
      <SleepFactorJudgementCriteriaDialog
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

function SleepFactorJudgementCriteriaTable() {
  return (
    <TableContainer
      sx={{
        borderRadius: 0,
        "& .MuiTableHead-root": {
          "& .MuiTableCell-root": {
            backgroundColor: "#F6F5F4",
            borderTop: "1px solid #d8d7d6",
            px: 0,
            py: "13.1px",
          },
          "& .MuiTableCell-root:nth-of-type(3)": {
            width: "64px",
          },
          "& .MuiTableCell-root:not(:nth-of-type(3))": {
            width: "128px",
          },
          "& .MuiTypography-root": {
            lineHeight: "100%",
          },
        },
        "& .MuiTableBody-root": {
          "& .MuiTableCell-root": {
            px: 2,
            py: 3,
            verticalAlign: "top",
          },
        },
      }}
    >
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <Stack
                direction="row"
                alignItems="center"
                sx={{
                  pl: 2,
                }}
              >
                <Brightness1Outlined
                  sx={{
                    color: "primary.main",
                    fontSize: "12.8px",
                  }}
                />
                <Typography variant="caption" color="text.primary">
                  の日数
                </Typography>
              </Stack>
            </TableCell>
            <TableCell>
              <Stack
                direction="row"
                alignItems="center"
                sx={{
                  borderLeft: "1px solid #d8d7d6",
                  pl: 2,
                }}
              >
                <CloseOutlined
                  sx={{
                    color: "error.main",
                    fontSize: "12.8px",
                  }}
                />
                <Typography variant="caption" color="text.primary">
                  の日数
                </Typography>
              </Stack>
            </TableCell>
            <TableCell>
              <Stack
                direction="row"
                justifyContent="center"
                sx={{
                  borderLeft: "1px solid #d8d7d6",
                }}
              >
                <Typography variant="caption" color="text.primary">
                  判定
                </Typography>
              </Stack>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* TODO: 本来はこのあたりのTypographyにvariant="body3"を使いたい */}
          <TableRow>
            <TableCell
              rowSpan={2}
              sx={{ borderRight: "1px dashed rgba(0, 0, 0, 0.12)" }}
            >
              <Typography sx={{ fontSize: "13px" }}>5日以上</Typography>
            </TableCell>
            <TableCell sx={{ borderRight: "1px solid rgba(0, 0, 0, 0.12)" }}>
              <Typography sx={{ fontSize: "13px" }}>なし</Typography>
            </TableCell>
            <TableCell align="center">
              <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                良好
              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ borderRight: "1px solid rgba(0, 0, 0, 0.12)" }}>
              <Typography sx={{ fontSize: "13px" }}>1～2日</Typography>
            </TableCell>
            <TableCell align="center">
              <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                注意
              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell
              rowSpan={2}
              sx={{ borderRight: "1px dashed rgba(0, 0, 0, 0.12)" }}
            >
              <Typography sx={{ fontSize: "13px" }}>4日以下</Typography>
            </TableCell>
            <TableCell sx={{ borderRight: "1px solid rgba(0, 0, 0, 0.12)" }}>
              <Typography sx={{ fontSize: "13px" }}>0～2日</Typography>
            </TableCell>
            <TableCell align="center">
              <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                注意
              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ borderRight: "1px solid rgba(0, 0, 0, 0.12)" }}>
              <Typography sx={{ fontSize: "13px" }}>3日以上</Typography>
            </TableCell>
            <TableCell align="center">
              <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                警戒
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}
